import {combineReducers, applyMiddleware, compose, Middleware} from 'redux';
import {createBrowserHistory} from 'history';
import dynamicMiddlewares, {addMiddleware, removeMiddleware as _removeMiddleware} from 'redux-dynamic-middlewares';
import {createStaticReducers, createStaticMiddlewares} from "./modules/app"
import {configureStore} from "@reduxjs/toolkit";
import {DynamicStateTypes} from "./types/dynamicStateTypes"
import {getPreviewPrefix, isPreviewMode} from "utils/previewMode";

// In quick-change preview mode the URL is
//   https://staging.console.workloads.netapp.com/previews/pr-<n>/index.html
// and React Router needs to treat that prefix as the app root,
// otherwise:
//   1. None of the explicit routes (`/`, `/wlmfsx/*`, `/wlmconsole/*`, ...)
//      match the raw pathname.
//   2. The catch-all `<Route path="*" element={<Navigate to="/" replace />}>`
//      in App.tsx fires and rewrites the URL to `/`, throwing the user
//      onto live-staging-equivalent navigation and stripping the
//      preview context that `appMiddleware.getUrlPrefix()` and
//      `authMiddleware` rely on. (Symptom: endless reload loop after
//      the auth flow tries to bounce the user back into the preview.)
// Setting basename here makes the router strip the preview prefix
// before matching, so `/previews/pr-14345/index.html` becomes
// `/index.html` (catch-all → `/`) and the URL ends up at
// `/previews/pr-14345/` rendering <HomepageDashboard/>. Navigations
// elsewhere (e.g. `/wlmfsx/dashboard`) get the preview prefix
// prepended automatically.
//
// This is computed once at module load time from window.location.pathname.
// That's the URL the user opened, so it's a stable one-shot decision
// per page load.
export const history = createBrowserHistory({
    window,
    ...(isPreviewMode() ? { basename: getPreviewPrefix() } : {})
});

const initialState = {};

const staticReducers = createStaticReducers();

type asyncReducersType = {
    [key: string]: any
};

const asyncReducers: asyncReducersType = {};

const createReducer = () => {
    return combineReducers({
        ...staticReducers,
        ...asyncReducers
    });
};

const store = configureStore({
        reducer: createReducer(),
        preloadedState: initialState,
        enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(compose(applyMiddleware(dynamicMiddlewares))),
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: false
            })
    }
);

export const registerReducer = (key: string, asyncReducer: any) => {
    asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer())
};

export const removeReducer = (key: string) => {
    delete asyncReducers[key];
    store.replaceReducer(createReducer())
};

type namedMiddlewaresType = {
    [key: string]: Middleware
};

const namedMiddlewares: namedMiddlewaresType = {};
export const registerMiddleware = (middleware: Middleware, key?: string) => {
    if (key) {
        const existing = namedMiddlewares[key];
        if (existing) {
            _removeMiddleware(existing);
        }
        namedMiddlewares[key] = middleware;
    }
    addMiddleware(middleware)
};

export const removeMiddleware = (key: string) => {
    const middleware = namedMiddlewares[key];
    delete namedMiddlewares[key];
    _removeMiddleware(middleware);
};

addMiddleware(...createStaticMiddlewares());

type registeredModulesType = {
    [key: string]: boolean
};

const registeredModules: registeredModulesType = {}
export const registerModule = (name: string) => {
    registeredModules[name] = true;
}

export const removeModule = (name: string) => {
    registeredModules[name] = false;
}

export const isModuleRegistered = (name: string) => {
    return registeredModules[name] || false;
}

export type ActionType = {
    type: string,
    payload: any
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState> & DynamicStateTypes
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

export default store;